import {
  isIsolatedPageSlotId,
  registryFormIdFromSlotId,
  resolveStableParentPageSlotId,
} from '@/common/utils/registryFormIdFromSlotId'
import { extractFormFid } from '@/stores/app'
import { formHasRenderableLayout } from '@/services/parse/parse.service'

export type DocumentTabType = 'list' | 'document'

export const DIR_HOME_PAGE_WINDOW_TYPE = 'dir-home-page'

export function normalizeCustomWindowType(raw: unknown): string | undefined {
  const s = String(raw ?? '').trim()
  return s || undefined
}

export function stampCustomWindowTypeOnPageData(
  data: Record<string, unknown>,
  customWindowType: unknown,
): void {
  const normalized = normalizeCustomWindowType(customWindowType)
  if (normalized) {
    data.$customWindowType = normalized
  }
}

export function isDirHomePageWorkspace(
  pageData: Record<string, unknown> | null | undefined,
  slotId?: string | null,
): boolean {
  if (normalizeCustomWindowType(pageData?.$customWindowType) !== DIR_HOME_PAGE_WINDOW_TYPE) {
    return false
  }
  const slot = String(slotId ?? '').trim()
  if (slot && isIsolatedPageSlotId(slot)) {
    return false
  }
  return true
}

export function shouldUseAdjacentSplitPreview(
  doActionForAdjacentWindow: boolean | undefined,
  slotId: string | null | undefined,
  pagesData: Record<string, unknown>,
): boolean {
  if (!doActionForAdjacentWindow) {
    return false
  }
  const slot = String(slotId ?? '').trim()
  if (!slot) {
    return false
  }
  return isDirHomePageWorkspace(pagesData[slot] as Record<string, unknown> | undefined, slot)
}

export type AdjacentSplitViewContext = {
  currentPageId: string | null | undefined
  adjacentPageId: string | null | undefined
  currentPageData: Record<string, unknown> | null | undefined
  adjacentPageData: Record<string, unknown> | null | undefined
  activeTabType: DocumentTabType | null | undefined
  routeName: string | null | undefined
  pagesData?: Record<string, unknown>
  openTabIds?: Iterable<string>
}

export function adjacentParentMatchesMain(
  mainId: string | null | undefined,
  adjParentId: string | null | undefined,
  pagesData: Record<string, unknown>,
  openTabIds?: Iterable<string>,
): boolean {
  const main = String(mainId ?? '').trim()
  const parent = String(adjParentId ?? '').trim()
  if (!main || !parent) {
    return false
  }
  if (parent === main) {
    return true
  }

  const mainPage = pagesData[main] as Record<string, unknown> | undefined
  const parentPage = pagesData[parent] as Record<string, unknown> | undefined
  const mainFid = mainPage ? String(mainPage.$fid ?? extractFormFid(mainPage) ?? '').trim() : ''
  const parentFid = parentPage
    ? String(parentPage.$fid ?? extractFormFid(parentPage) ?? '').trim()
    : ''

  if (mainFid && parentFid && mainFid === parentFid) {
    return true
  }
  if (mainFid && (parent === mainFid || registryFormIdFromSlotId(parent) === mainFid)) {
    return true
  }
  if (parentFid && (main === parentFid || registryFormIdFromSlotId(main) === parentFid)) {
    return true
  }

  const stableMain = resolveStableParentPageSlotId(main, pagesData, openTabIds)
  const stableParent = resolveStableParentPageSlotId(parent, pagesData, openTabIds)

  if (stableMain && parent === stableMain) {
    return true
  }
  if (stableParent && main === stableParent) {
    return true
  }
  if (stableMain && stableParent && stableMain === stableParent) {
    return true
  }

  const mainReg = registryFormIdFromSlotId(main)
  const parentReg = registryFormIdFromSlotId(parent)
  if (mainReg && parentReg && mainReg === parentReg) {
    return true
  }

  return false
}

export function resolveAdjacentPageIdForMain(
  mainId: string | null | undefined,
  pagesData: Record<string, unknown>,
  lastAdjacentByParent: Record<string, string>,
  openTabIds?: Iterable<string>,
): string | null {
  const main = String(mainId ?? '').trim()
  if (!main) {
    return null
  }

  const isValidAdjacent = (adjId: string): boolean => {
    const pageData = pagesData[adjId] as Record<string, unknown> | undefined
    if (pageData?.$isAdjacentWindow !== true) {
      return false
    }
    return adjacentParentMatchesMain(
      main,
      String(pageData.$parentPageId ?? ''),
      pagesData,
      openTabIds,
    )
  }

  const lookupKeys = [main]
  const stableMain = resolveStableParentPageSlotId(main, pagesData, openTabIds)
  if (stableMain && stableMain !== main) {
    lookupKeys.push(stableMain)
  }
  const mainReg = registryFormIdFromSlotId(main)
  if (mainReg && mainReg !== main && !lookupKeys.includes(mainReg)) {
    lookupKeys.push(mainReg)
  }

  for (const key of lookupKeys) {
    const lastOpenedId = lastAdjacentByParent[key]
    if (lastOpenedId && isValidAdjacent(lastOpenedId)) {
      return lastOpenedId
    }
  }

  const lastAdjacentPage = Object.entries(pagesData)
    .filter(([adjId, pdata]) => {
      const pd = pdata as Record<string, unknown>
      return (
        pd.$isAdjacentWindow === true &&
        adjacentParentMatchesMain(main, String(pd.$parentPageId ?? ''), pagesData, openTabIds)
      )
    })
    .pop()

  if (!lastAdjacentPage) {
    return null
  }
  return lastAdjacentPage[0]
}

/**
 * Swing parity (DirectoryHomePage): split on dir-home-page list workspace with adjacent preview.
 */
export function shouldShowAdjacentSplit(ctx: AdjacentSplitViewContext): boolean {
  const mainId = String(ctx.currentPageId ?? '').trim()
  const adjId = String(ctx.adjacentPageId ?? '').trim()
  if (!mainId || !adjId) {
    return false
  }

  const current = ctx.currentPageData
  if (current?.$isAdjacentWindow === true) {
    return false
  }
  if (!isDirHomePageWorkspace(current, mainId)) {
    return false
  }

  const adj = ctx.adjacentPageData
  if (!adj || adj.$isAdjacentWindow !== true) {
    return false
  }
  const pagesData = ctx.pagesData ?? {}
  const openTabIds = ctx.openTabIds
  const parentMatches = adjacentParentMatchesMain(
    mainId,
    String(adj.$parentPageId ?? ''),
    pagesData,
    openTabIds,
  )
  if (!parentMatches) {
    return false
  }

  const isListWorkspace =
    ctx.routeName === 'platform' || ctx.activeTabType === 'list'
  if (!isListWorkspace) {
    return false
  }

  if (formHasRenderableLayout(adj.form)) {
    return true
  }

  const sessionId = adj.sessionId ?? adj.$sessionId
  return sessionId != null && String(sessionId).trim() !== ''
}

export function readPreviewWidthPercent(
  pageData: Record<string, unknown> | null | undefined,
): number | null {
  if (!pageData || typeof pageData !== 'object') {
    return null
  }
  const form = pageData.form as Record<string, unknown> | undefined
  if (!form || typeof form !== 'object') {
    return null
  }
  const inner = form.form as Record<string, unknown> | undefined
  const previewWidth =
    (form.previewWidth as { _text?: unknown } | undefined)?._text ??
    form.previewWidth ??
    (inner?.previewWidth as { _text?: unknown } | undefined)?._text ??
    inner?.previewWidth ??
    (form._attributes as { previewWidth?: unknown } | undefined)?.previewWidth ??
    (inner?._attributes as { previewWidth?: unknown } | undefined)?.previewWidth

  if (previewWidth == null || previewWidth === '') {
    return null
  }
  const parsed = parseFloat(String(previewWidth))
  if (!Number.isFinite(parsed)) {
    return null
  }
  return Math.max(0, Math.min(100, parsed * 100))
}
